import { useEffect, useState } from "react";
import axios from "axios";

const API   = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("kids_parent_token");

/**
 * Resolves a game's playable config for the kids game engines.
 *
 * • For the built-in seeded games we keep rich hardcoded config keyed by slug
 *   (passed in as `hardcoded`) — these resolve instantly, no network call.
 * • For admin-created games there is no hardcoded entry, so we fetch the game
 *   record (`GET /kids/games/single/{id}`) and use its `content` JSON. Admins
 *   author `content` in the SAME shape as a hardcoded entry, so the engines
 *   don't need to branch. `defaults` fills in any presentation fields the
 *   admin left out (gradient, accent, emoji, etc.).
 *
 * @returns {{ game: object|null, resolving: boolean, notFound: boolean }}
 */
export default function useGameContent({ slug, gameId, hardcoded = {}, defaults = {} }) {
  const hc = hardcoded[slug];

  const [game, setGame]           = useState(hc || null);
  const [resolving, setResolving] = useState(!hc);
  const [notFound, setNotFound]   = useState(false);

  useEffect(() => {
    if (hc) return;                       // built-in game — already resolved
    if (!gameId) { setResolving(false); setNotFound(true); return; }

    let alive = true;
    axios.get(`${API}/api/kids/games/single/${gameId}`, {
      headers: { Authorization: `Bearer ${token()}` },
    })
      .then((res) => {
        if (!alive) return;
        const g = res.data?.data;
        const content = g?.content;
        if (g && content && typeof content === "object") {
          setGame({ ...defaults, ...content, title: content.title || g.name });
        } else {
          setNotFound(true);
        }
      })
      .catch(() => { if (alive) setNotFound(true); })
      .finally(() => { if (alive) setResolving(false); });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hc, gameId]);

  return { game, resolving, notFound };
}
